import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Chip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { safeAlpha } from "../../../utils/colorUtils";
import api from "../../../utils/api";

const ProspectCard = ({
  prospect,
  onAddNote,
  onSetColor,
  onViewProfile,
  isDragging = false,
}) => {
  const [latestNote, setLatestNote] = useState(null);
  const [isLoadingNote, setIsLoadingNote] = useState(false);

  // Fetch the latest note from the prospect entity
  useEffect(() => {
    const fetchLatestNote = async () => {
      // Handle both data structures: prospect.prospect can be an object or just an ID string
      const prospectId =
        typeof prospect.prospect === "string"
          ? prospect.prospect
          : prospect.prospect?._id;

      if (!prospectId) return;

      setIsLoadingNote(true);
      try {
        const response = await api.prospectNotes.getLatestNote(prospectId);
        if (response.success && response.data) {
          setLatestNote(response.data);
        }
      } catch (error) {
        console.error("Error fetching latest note:", error);
      } finally {
        setIsLoadingNote(false);
      }
    };

    fetchLatestNote();
  }, [prospect.prospect, prospect.noteRefreshTrigger]);

  // Simple style for card
  const style = {
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  // Determine card background color based on prospect color
  const cardBgColor = prospect.color
    ? safeAlpha(prospect.color, 0.1)
    : "background.paper";

  // Determine card border color based on prospect color
  const cardBorderColor = prospect.color || "divider";

  return (
    <Card
      style={style}
      sx={{
        mb: 1,
        backgroundColor: cardBgColor,
        borderLeft: "4px solid",
        borderColor: cardBorderColor,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 3,
          cursor: "pointer",
        },
      }}
      onClick={() =>
        onViewProfile && onViewProfile(prospect.id || prospect._id)
      }
    >
      <CardContent sx={{ p: 1.5, pb: 0.5 }}>
        {/* Prospect header with image and name */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Avatar
            src={prospect.profilePicture}
            alt={`${prospect.firstName} ${prospect.lastName}`}
            sx={{ width: 40, height: 40, mr: 1.5 }}
          >
            {prospect.profilePicture ? null : <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" noWrap>
              {prospect.firstName} {prospect.lastName}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              <Chip
                label={prospect.position}
                size="small"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              <Chip
                label={prospect.classYear}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Box>
          </Box>
        </Box>

        {/* School and location */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SchoolIcon
              fontSize="small"
              sx={{ mr: 0.5, color: "text.secondary", fontSize: "0.9rem" }}
            />
            <Typography variant="body2" noWrap>
              {prospect.highSchool}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocationOnIcon
              fontSize="small"
              sx={{ mr: 0.5, color: "text.secondary", fontSize: "0.9rem" }}
            />
            <Typography variant="body2" noWrap>
              {prospect.city}, {prospect.state}
            </Typography>
          </Box>
        </Box>

        {/* Latest note preview from prospect entity */}
        {isLoadingNote ? (
          <Box
            sx={{
              p: 1,
              mt: 1,
              bgcolor: "background.default",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={12} />
            <Skeleton variant="text" width="80%" height={14} sx={{ flex: 1 }} />
          </Box>
        ) : (
          latestNote && (
            <Box
              sx={{
                p: 1,
                mt: 1,
                bgcolor: "background.default",
                borderRadius: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                maxHeight: "60px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {latestNote.text}
            </Box>
          )
        )}
      </CardContent>

      <CardActions sx={{ p: 0.5, justifyContent: "flex-end" }}>
        <Tooltip title="Add Note">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddNote();
            }}
          >
            <NoteAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Set Card Color">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSetColor();
            }}
          >
            <ColorLensIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="View Profile">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onViewProfile();
            }}
          >
            <PersonIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default ProspectCard;
